// src/pages/OrdersPage.js
import React, { useState, useEffect, useCallback } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import { updateOrderStatus } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { connectSocket } from '../services/socket';
import api from '../services/api';
import ListFilters from '../components/ListFilters';
import Pagination from '../components/Pagination';
import { downloadCSV } from '../utils/downloadCSV';

const STATUS_NEXT = { PENDING:'PREPARING', PREPARING:'READY', READY:'COMPLETED' };

const STATUS_STYLE = {
  AWAITING_PAYMENT: { badge:'badge-amber', label:'Awaiting Payment' },
  PENDING:          { badge:'badge-blue',  label:'Pending'          },
  PREPARING:        { badge:'badge-amber', label:'Preparing'        },
  READY:            { badge:'badge-green', label:'Ready'            },
  COMPLETED:        { badge:'badge-green', label:'Completed'        },
  CANCELLED:        { badge:'badge-red',   label:'Cancelled'        },
  REFUNDED:         { badge:'badge-red',   label:'Refunded'         },
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function OrdersPage() {
  const { vendor } = useAuth();

  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [orders,     setOrders]     = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page,       setPage]       = useState(1);
  const [dateRange,  setDateRange]  = useState({ startDate: todayStr(), endDate: todayStr() });
  const [filters,    setFilters]    = useState({ status: '', paymentMode: '', search: '' });
  const [cancelling, setCancelling] = useState(null);
  const [refunding,  setRefunding]  = useState(null);
  const [loading,    setLoading]    = useState(false);

  const fetchOrders = useCallback(async () => {
    if (!vendor?.id) return;
    setLoading(true);
    try {
      const params = {
        page, limit: 50,
        startDate: dateRange.startDate,
        endDate:   dateRange.endDate,
        ...(filters.status      && { status:      filters.status }),
        ...(filters.paymentMode && { paymentMode: filters.paymentMode }),
        ...(filters.search      && { search:      filters.search }),
      };
      const r = await api.get('/orders', { params });
      setOrders(r.data.data || []);
      setPagination(r.data.pagination || null);
    } catch (_) {}
    finally { setLoading(false); }
  }, [vendor, page, dateRange, filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Socket: real-time updates push into the current view only when they match today
  useEffect(() => {
    if (!vendor?.id) return;
    const socket = connectSocket(vendor.id);

    socket.on('order:awaiting', (order) => {
      setOrders(prev => prev.find(o => o.id === order.id) ? prev : [order, ...prev]);
    });
    socket.on('order:new', (order) => {
      setOrders(prev => prev.find(o => o.id === order.id)
        ? prev.map(o => o.id === order.id ? order : o)
        : [order, ...prev]);
    });
    socket.on('order:status', ({ id, status }) => {
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
    });

    return () => {
      socket.off('order:awaiting');
      socket.off('order:new');
      socket.off('order:status');
    };
  }, [vendor]);

  const handleAdvance = async (id, currentStatus) => {
    const next = STATUS_NEXT[currentStatus];
    if (!next) return;
    try {
      await updateOrderStatus(id, next);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: next } : o));
    } catch (_) {}
  };

  const handleCancelUnpaid = async (id) => {
    setCancelling(id);
    try {
      await api.patch(`/orders/${id}/cancel-unpaid`);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: 'CANCELLED', paymentStatus: 'FAILED' } : o));
    } catch (_) {
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: 'CANCELLED' } : o));
    } finally { setCancelling(null); }
  };

  const handleRefund = (id) => {
    setConfirm({
      message: 'Refund this order? The payment will be returned to the customer.',
      confirmLabel: 'Issue Refund',
      onConfirm: async () => {
        setConfirm(null);
        setRefunding(id);
        try {
          await api.post('/payments/refund', { orderId: id });
          setOrders(prev => prev.map(o =>
            o.id === id ? { ...o, paymentStatus: 'REFUNDED', status: 'CANCELLED' } : o
          ));
        } catch (err) {
          alert(err.response?.data?.error || 'Refund failed');
        } finally {
          setRefunding(null);
        }
      },
    });
  };

  const handleDateChange =({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
    setPage(1);
  };

  const counts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="page">
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <div className="page-title">Orders</div>
      <div className="page-sub">Real-time order management — all orders recorded for analysis</div>

      {/* Status summary pills */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
        {Object.entries(STATUS_STYLE).map(([status, { badge, label }]) =>
          counts[status] ? (
            <span key={status} className={`badge ${badge}`}>{label}: {counts[status]}</span>
          ) : null
        )}
      </div>

      <ListFilters
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        maxRangeDays={2}
        onDateChange={handleDateChange}
        filters={[
          {
            key: 'status', label: 'Status', value: filters.status,
            options: ['', 'AWAITING_PAYMENT', 'PENDING', 'PREPARING', 'READY', 'COMPLETED', 'CANCELLED'],
            onChange: v => { setFilters(f => ({ ...f, status: v })); setPage(1); },
          },
          {
            key: 'paymentMode', label: 'Payment', value: filters.paymentMode,
            options: ['', 'CARD', 'CASH', 'UPI', 'WALLET', 'TAP'],
            onChange: v => { setFilters(f => ({ ...f, paymentMode: v })); setPage(1); },
          },
        ]}
        search={filters.search}
        onSearchChange={v => { setFilters(f => ({ ...f, search: v })); setPage(1); }}
        onExport={() => downloadCSV('/vendors/me/reports/daily/csv', dateRange, 'orders.csv')}
      />

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading && (
          <div style={{ padding:12, textAlign:'center', fontSize:12, color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>
            Loading…
          </div>
        )}
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr>
              {['Order #','Items','Total','Payment','POS','Time','Status','Action'].map(h => (
                <th key={h} style={{ textAlign:'left', padding:'11px 14px', fontSize:11, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.5px', borderBottom:'1px solid var(--border)' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && !loading && (
              <tr>
                <td colSpan={8} style={{ padding:40, textAlign:'center', color:'var(--text3)' }}>
                  No records found for this period
                </td>
              </tr>
            )}
            {orders.map(o => {
              const ss = STATUS_STYLE[o.status] || { badge:'badge-amber', label:o.status };
              const payBadge = o.paymentStatus === 'PAID' ? 'badge-green'
                : o.paymentStatus === 'REFUNDED' ? 'badge-red'
                : o.paymentStatus === 'FAILED' ? 'badge-red'
                : 'badge-amber';
              const canRefund = o.paymentStatus === 'PAID' && o.status !== 'AWAITING_PAYMENT';
              return (
                <tr key={o.id} style={{ borderBottom:'1px solid var(--border)', opacity: o.status === 'CANCELLED' ? .55 : 1 }}>
                  <td style={{ padding:'11px 14px', fontWeight:700, color:'var(--accent)' }}>{o.orderNumber}</td>
                  <td style={{ padding:'11px 14px', maxWidth:200 }}>
                    {o.items?.map((i, idx) => (
                      <span key={idx} className="tag">{i.name} ×{i.quantity||i.qty}</span>
                    ))}
                  </td>
                  <td style={{ padding:'11px 14px', fontWeight:700 }}>AED {Number(o.total).toFixed(2)}</td>
                  <td style={{ padding:'11px 14px' }}>
                    <span className={`badge ${payBadge}`}>
                      {o.paymentStatus}
                    </span>
                  </td>
                  <td style={{ padding:'11px 14px' }}>
                    {o.posPushStatus === 'SENT'   && <span style={{ fontSize:11, color:'var(--green)',  fontWeight:600, whiteSpace:'nowrap' }}>✓ Sent</span>}
                    {o.posPushStatus === 'FAILED'  && <span style={{ fontSize:11, color:'var(--accent)', fontWeight:600, whiteSpace:'nowrap' }}>⚠ Failed</span>}
                    {o.posPushStatus === 'SKIPPED' && <span style={{ fontSize:11, color:'var(--text3)',  whiteSpace:'nowrap' }}>— Skip</span>}
                    {!o.posPushStatus              && <span style={{ fontSize:11, color:'var(--text3)' }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 14px', color:'var(--text2)', whiteSpace:'nowrap' }}>
                    {new Date(o.createdAt).toLocaleTimeString('en',{hour:'2-digit',minute:'2-digit'})}
                  </td>
                  <td style={{ padding:'11px 14px' }}>
                    <span className={`badge ${ss.badge}`}>{ss.label}</span>
                  </td>
                  <td style={{ padding:'11px 14px' }}>
                    {o.status === 'AWAITING_PAYMENT' ? (
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={cancelling === o.id}
                        onClick={() => setConfirm({ message: 'Cancel this order? Stock will be restored.', confirmLabel: 'Cancel order', onConfirm: () => handleCancelUnpaid(o.id) })}>
                        {cancelling === o.id ? '...' : '✕ Cancel'}
                      </button>
                    ) : STATUS_NEXT[o.status] ? (
                      <button className="btn btn-ghost btn-sm" onClick={() => handleAdvance(o.id, o.status)}>
                        → {STATUS_NEXT[o.status]}
                      </button>
                    ) : !canRefund && (
                      <span style={{ color:'var(--text3)', fontSize:12 }}>—</span>
                    )}
                    {canRefund && (
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ marginLeft: 6 }}
                        disabled={refunding === o.id}
                        onClick={() => handleRefund(o.id)}>
                        {refunding === o.id ? '...' : '↩ Refund'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pagination?.page || page}
        totalPages={pagination?.totalPages || 0}
        total={pagination?.total || 0}
        limit={pagination?.limit || 50}
        onPageChange={p => setPage(p)}
      />
    </div>
  );
}
